import {
  CircleBottom,
  NavigateLeft,
  NavigateRight,
} from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import classnames from 'classnames';
import { Day, endOfMonth, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ForwardedRef,
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import ReactDatePicker from 'react-datepicker';

import { Button } from '../../Button/Button';
import { Chip } from '../../Chip';
import { RawDropdown } from '../../RawDropdown';

import styles from './DatePicker.module.scss';
import { DatePickerActions, DatePickerProps } from './DatePicker.types';

const DatePickerInner = <
  CustomModifierNames extends string = never,
  WithRange extends boolean | undefined = undefined,
>(
  {
    locale = fr,
    monthsShown = 1,
    openToDate,
    events,
    calendarClassName,
    dayClassName,
    onChange,
    ...props
  }: DatePickerProps<CustomModifierNames, WithRange>,
  ref: ForwardedRef<DatePickerActions>
) => {
  const days = useMemo(() => {
    return Object.fromEntries(
      ([...Array(7).fill(null).keys()] as Day[]).map((day): [string, string] => [
        locale.localize?.day(day) ?? '',
        locale.localize?.day(day, { width: 'abbreviated' }).slice(0, 3).toLowerCase() ?? '',
      ])
    );
  }, [locale]);

  const [openMonth, setOpenMonth] = useState(openToDate ?? props.selected ?? new Date());

  useImperativeHandle(ref, () => ({
    goToToday: () => setOpenMonth(new Date()),
  }));

  const findEvent = useCallback(
    (date?: Date) => {
      if (!date) return undefined;
      const event = events?.find(
        event =>
          event.year === date.getFullYear() &&
          event.month === date.getMonth() &&
          event.day === date.getDate()
      );
      if (!event) return undefined;
      if (
        date.getFullYear() === openMonth?.getFullYear() &&
        date.getMonth() === openMonth.getMonth()
      )
        return event;
      return { ...event, color: 'disabled' as const };
    },
    [events, openMonth]
  );

  return (
    <ReactDatePicker<CustomModifierNames, WithRange>
      inline
      fixedHeight
      locale={locale}
      calendarClassName={classnames(styles.calendar, calendarClassName)}
      formatWeekDay={day => days[day]}
      openToDate={openMonth}
      onMonthChange={setOpenMonth}
      onYearChange={setOpenMonth}
      monthsShown={monthsShown}
      renderCustomHeader={({
        monthDate,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
        changeMonth,
        changeYear,
        customHeaderCount,
      }) => {
        const maxYear = props.maxDate?.getFullYear() ?? 2100;
        const minYear = props.minDate?.getFullYear() ?? 1900;
        const years = [...Array(maxYear + 1 - minYear).keys()]
          .reverse()
          .map(year => year + minYear);
        return (
          <div className={styles.header} key="header">
            <div className={styles.monthYear}>
              {props.showYearDropdown && customHeaderCount === 0 ? (
                <>
                  {format(monthDate, 'MMMM', { locale }).toLowerCase()}
                  <RawDropdown<number>
                    trigger={
                      <Chip>
                        {monthDate.getFullYear()}
                        <CircleBottom />
                      </Chip>
                    }
                    position="bottom left"
                    items={years}
                    keyValueExtractor={value => ({
                      key: value.toString(),
                      value: value.toString(),
                    })}
                    selectedItem={monthDate.getFullYear()}
                    onSelectItem={item => {
                      if (!item) return;
                      changeMonth(monthDate.getMonth());
                      changeYear(item);
                    }}
                  />
                </>
              ) : (
                format(monthDate, 'MMMM yyyy', { locale }).toLowerCase()
              )}
            </div>
            {customHeaderCount === monthsShown - 1 && (
              <>
                <Button
                  variant="tertiary"
                  size="medium"
                  disabled={prevMonthButtonDisabled}
                  onClick={decreaseMonth}
                  type="button"
                >
                  <NavigateLeft />
                </Button>
                <Button
                  variant="tertiary"
                  size="medium"
                  disabled={nextMonthButtonDisabled}
                  onClick={increaseMonth}
                  type="button"
                >
                  <NavigateRight />
                </Button>
              </>
            )}
          </div>
        );
      }}
      dayClassName={date =>
        classnames(dayClassName?.(date), {
          [styles.first]: date?.getDay() === locale.options?.weekStartsOn || date.getDate() === 1,
          [styles.last]:
            date?.getDay() === ((locale.options?.weekStartsOn ?? 0) - 1) % 6 ||
            date.getDate() === endOfMonth(date).getDate(),
        })
      }
      renderDayContents={(day, date) => {
        const event = findEvent(date);
        return (
          <div className={styles.day}>
            {day}
            {event && (
              <div
                className={classnames(
                  styles.event,
                  styles[event.color],
                  event.large ? styles.large : styles.small
                )}
              />
            )}
          </div>
        );
      }}
      timeCaption="heure"
      onChange={(
        date: WithRange extends false | undefined ? Date | null : [Date | null, Date | null]
      ) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- This is ok because of the Array.isArray test
        // @ts-ignore
        onChange(Array.isArray(date) ? date.map(d => d ?? undefined) : (date ?? undefined));
        setTimeout(() =>
          document
            .getElementsByClassName('react-datepicker__time-list-item--selected')[0]
            ?.scrollIntoView({ behavior: 'auto', block: 'center' })
        );
      }}
      timeIntervals={60}
      {...props}
    />
  );
};

export const DatePicker = forwardRef(DatePickerInner) as <
  CustomModifierNames extends string = never,
  WithRange extends boolean | undefined = undefined,
>(
  props: DatePickerProps<CustomModifierNames, WithRange> & { ref?: Ref<DatePickerActions> }
) => ReturnType<typeof DatePickerInner>;
